/** Admin Users management — user list, role editor, deactivate, invite. */
import { useState, useEffect } from 'react';
import { api } from '../../auth/api';
import { UserPlus, UserCog, UserX } from 'lucide-react';

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({ full_name: '', email: '', role: 'viewer', password: '' });
    const [formErrors, setFormErrors] = useState('');

    useEffect(() => { loadUsers(); }, []);

    const loadUsers = async () => {
        try {
            const res = await api.get('/auth/users');
            setUsers(res.data.users || []);
        } catch (e) {
            setError('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setFormErrors('');
        try {
            await api.post('/auth/users', formData);
            setShowForm(false);
            setFormData({ full_name: '', email: '', role: 'viewer', password: '' });
            loadUsers();
        } catch (err) {
            setFormErrors(err.response?.data?.detail || 'Failed to create user');
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            await api.patch(`/auth/users/${userId}`, { role: newRole });
            loadUsers();
        } catch (err) {
            console.error('Role update failed:', err);
        }
    };

    const handleDeactivate = async (userId) => {
        if (!confirm('Deactivate this user?')) return;
        try {
            await api.patch(`/auth/users/${userId}`, { active: false });
            loadUsers();
        } catch (err) {
            console.error('Deactivation failed:', err);
        }
    };

    if (loading) return <div className="p-6 text-slate-400">Loading users...</div>;

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-xl font-bold text-white">User Management</h1>
                <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 rounded bg-amber-500/20 border border-amber-500/30 text-amber-400 hover:bg-amber-500/30 text-sm">
                    <UserPlus size={16} /> Invite User
                </button>
            </div>

            {error && <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">{error}</div>}

            {/* Users table */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm text-slate-300">
                    <thead className="bg-slate-700/50 text-slate-400">
                        <tr>
                            <th className="px-4 py-3 text-left">Name</th>
                            <th className="px-4 py-3 text-left">Email</th>
                            <th className="px-4 py-3 text-left">Role</th>
                            <th className="px-4 py-3 text-left">Status</th>
                            <th className="px-4 py-3 text-left">Last Login</th>
                            <th className="px-4 py-3 text-left">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(u => (
                            <tr key={u.id} className="border-t border-slate-700/50">
                                <td className="px-4 py-3 font-medium text-white">{u.full_name}</td>
                                <td className="px-4 py-3">{u.email}</td>
                                <td className="px-4 py-3">
                                    <select
                                        value={u.role}
                                        onChange={e => handleRoleChange(u.id, e.target.value)}
                                        className="bg-slate-700 border-slate-600 rounded px-2 py-1 text-xs text-slate-200"
                                    >
                                        <option value="guest">Guest</option>
                                        <option value="viewer">Viewer</option>
                                        <option value="editor">Editor</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`px-2 py-0.5 rounded text-xs ${u.active !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                        {u.active !== false ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-xs">
                                    {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : 'Never'}
                                </td>
                                <td className="px-4 py-3">
                                    <button onClick={() => handleDeactivate(u.id)} className="text-red-400 hover:text-red-300" title="Deactivate">
                                        <UserX size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Invite form modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 w-full max-w-md">
                        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                            <UserPlus size={20} /> Invite New User
                        </h2>
                        {formErrors && <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">{formErrors}</div>}
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div>
                                <label className="text-sm font-medium text-slate-200 block mb-1">Full Name</label>
                                <input className="w-full bg-slate-700 border border-slate-600 rounded-lg p-2.5 text-slate-100 text-sm"
                                    value={formData.full_name} onChange={e => setFormData({ ...formData, full_name: e.target.value })} required />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-slate-200 block mb-1">Email (@namle-corp.com)</label>
                                <input className="w-full bg-slate-700 border border-slate-600 rounded-lg p-2.5 text-slate-100 text-sm"
                                    type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-slate-200 block mb-1">Role</label>
                                <select className="w-full bg-slate-700 border border-slate-600 rounded-lg p-2.5 text-slate-100 text-sm"
                                    value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })}>
                                    <option value="guest">Guest</option>
                                    <option value="viewer">Viewer</option>
                                    <option value="editor">Editor</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-slate-200 block mb-1">Initial Password</label>
                                <input className="w-full bg-slate-700 border border-slate-600 rounded-lg p-2.5 text-slate-100 text-sm"
                                    type="text" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} placeholder="CHANGE_ME_ON_FIRST_LOGIN" />
                            </div>
                            <div className="flex gap-3">
                                <button type="submit" className="flex-1 px-4 py-2 rounded bg-amber-500 hover:bg-amber-400 text-slate-900 text-sm font-medium">
                                    Create User
                                </button>
                                <button type="button" onClick={() => setShowForm(false)} className="flex-1 px-4 py-2 rounded bg-slate-700 hover:bg-slate-600 text-sm text-slate-300">
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}